import React, { useState, useEffect, useCallback } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Reminder {
  id: string;
  title: string;
  description?: string;
  dateTime: string;
  type: 'once' | 'daily' | 'weekly' | 'monthly';
  isEnabled: boolean;
  notificationIds?: string[];
  createdAt?: string;
}

const Reminders: React.FC = () => {
  const colors = getThemeColors('dark');

  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);

  // Form state
  const getDefaultDateTime = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0); // Set to 9:00 AM tomorrow
    return {
      date: tomorrow.toISOString().split('T')[0], // YYYY-MM-DD format
      time: '09:00' // HH:MM format
    };
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ...getDefaultDateTime(),
    type: 'once' as 'once' | 'daily' | 'weekly' | 'monthly',
    isEnabled: true,
  });

  useEffect(() => {
    loadReminders();
  }, []);

  const loadReminders = async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockReminders: Reminder[] = [
        {
          id: '1',
          title: 'Chama Meeting',
          description: 'Monthly chama meeting at community center',
          dateTime: '2025-01-15T14:00:00.000Z',
          type: 'monthly',
          isEnabled: true,
          createdAt: '2025-01-01T10:00:00.000Z'
        },
        {
          id: '2',
          title: 'Contribution Deadline',
          description: 'Monthly contribution payment due',
          dateTime: '2025-01-20T23:59:00.000Z',
          type: 'monthly',
          isEnabled: true,
          createdAt: '2025-01-01T10:00:00.000Z'
        },
        {
          id: '3',
          title: 'Budget Review',
          description: 'Review monthly expenses and savings',
          dateTime: '2025-01-25T19:00:00.000Z',
          type: 'monthly',
          isEnabled: false,
          createdAt: '2025-01-01T10:00:00.000Z'
        }
      ];

      // Sort by date/time
      const sortedReminders = mockReminders.sort((a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      );
      setReminders(sortedReminders);
    } catch (error) {
      console.error('Error loading reminders:', error);
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadReminders();
    setRefreshing(false);
  }, []);

  const handleAddReminder = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a reminder title');
      return;
    }

    // Validate date field
    if (!formData.date.trim()) {
      alert('Please select a date');
      return;
    }

    // Validate time field
    if (!formData.time.trim()) {
      alert('Please select a time');
      return;
    }

    // Combine date and time and validate
    const dateTimeString = `${formData.date}T${formData.time}:00`;
    const selectedDateTime = new Date(dateTimeString);
    const currentDateTime = new Date();

    if (isNaN(selectedDateTime.getTime())) {
      alert('Please enter a valid date and time');
      return;
    }

    if (selectedDateTime <= currentDateTime) {
      alert('Please select a future date and time');
      return;
    }

    try {
      const newReminder: Reminder = {
        id: `reminder_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        title: formData.title,
        description: formData.description,
        dateTime: `${formData.date}T${formData.time}:00.000Z`,
        type: formData.type,
        isEnabled: formData.isEnabled,
        createdAt: new Date().toISOString(),
      };

      const updatedReminders = [...reminders, newReminder].sort((a, b) =>
        new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      );

      setReminders(updatedReminders);
      resetForm();
      setShowAddModal(false);
      alert('Reminder added successfully!');
    } catch (error) {
      console.error('Error adding reminder:', error);
      alert('Failed to add reminder');
    }
  };

  const handleEditReminder = async () => {
    if (!editingReminder) return;

    if (!formData.title.trim()) {
      alert('Please enter a reminder title');
      return;
    }

    try {
      const updatedReminder: Reminder = {
        ...editingReminder,
        title: formData.title,
        description: formData.description,
        dateTime: `${formData.date}T${formData.time}:00.000Z`,
        type: formData.type,
        isEnabled: formData.isEnabled,
      };

      const updatedReminders = reminders.map(reminder =>
        reminder.id === editingReminder.id ? updatedReminder : reminder
      ).sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());

      setReminders(updatedReminders);
      resetForm();
      setShowAddModal(false);
      setEditingReminder(null);
      alert('Reminder updated successfully!');
    } catch (error) {
      console.error('Error updating reminder:', error);
      alert('Failed to update reminder');
    }
  };

  const handleDeleteReminder = async (reminder: Reminder) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;

    try {
      const updatedReminders = reminders.filter(r => r.id !== reminder.id);
      setReminders(updatedReminders);
      alert('Reminder deleted successfully!');
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Failed to delete reminder');
    }
  };

  const handleToggleReminder = async (reminder: Reminder) => {
    try {
      const updatedReminder = { ...reminder, isEnabled: !reminder.isEnabled };
      const updatedReminders = reminders.map(r =>
        r.id === reminder.id ? updatedReminder : r
      );
      setReminders(updatedReminders);
    } catch (error) {
      console.error('Error toggling reminder:', error);
      alert('Failed to update reminder');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      ...getDefaultDateTime(),
      type: 'once',
      isEnabled: true,
    });
  };

  const openAddModal = () => {
    resetForm();
    setEditingReminder(null);
    setShowAddModal(true);
  };

  const openEditModal = (reminder: Reminder) => {
    const reminderDate = new Date(reminder.dateTime);

    setFormData({
      title: reminder.title,
      description: reminder.description || '',
      date: reminderDate.toISOString().split('T')[0],
      time: reminderDate.toTimeString().slice(0, 5),
      type: reminder.type,
      isEnabled: reminder.isEnabled,
    });
    setEditingReminder(reminder);
    setShowAddModal(true);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'daily': return '📅';
      case 'weekly': return '📆';
      case 'monthly': return '🗓️';
      default: return '⏰';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'daily': return 'Daily';
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      default: return 'One-time';
    }
  };

  const isReminderPast = (dateTime: string) => {
    return new Date(dateTime) < new Date();
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const renderReminderItem = (reminder: Reminder) => {
    const isPast = isReminderPast(reminder.dateTime);
    const reminderDate = new Date(reminder.dateTime);

    return (
      <div
        key={reminder.id}
        style={{
          backgroundColor: colors.surface,
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          marginBottom: spacing.md,
          border: `1px solid ${colors.border}`,
          ...getShadowStyle('md'),
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}>
          <div style={{ flex: 1, marginRight: spacing.md }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: spacing.sm,
            }}>
              <h3 style={{
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
                margin: 0,
                flex: 1,
                marginRight: spacing.sm,
              }}>
                {reminder.title}
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: `${spacing.xs} ${spacing.sm}`,
                borderRadius: borderRadius.lg,
                backgroundColor: colors.primary + '15',
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                color: colors.primary,
              }}>
                <span style={{ marginRight: spacing.xs }}>{getTypeIcon(reminder.type)}</span>
                <span>{getTypeLabel(reminder.type)}</span>
              </div>
            </div>

            {reminder.description && (
              <p style={{
                fontSize: typography.fontSize.base,
                color: colors.textSecondary,
                margin: 0,
                marginBottom: spacing.sm,
                lineHeight: 1.4,
              }}>
                {reminder.description}
              </p>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: typography.fontSize.sm,
                color: colors.textSecondary,
              }}>
                <span style={{ marginRight: spacing.sm }}>📅</span>
                <span>{formatDateTime(reminder.dateTime)}</span>
              </div>

              {isPast && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${spacing.xs} ${spacing.sm}`,
                  borderRadius: borderRadius.md,
                  backgroundColor: colors.error + '15',
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.medium,
                  color: colors.error,
                }}>
                  <span style={{ marginRight: spacing.xs }}>⏰</span>
                  <span>Past</span>
                </div>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing.sm,
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={reminder.isEnabled}
                onChange={() => handleToggleReminder(reminder)}
                style={{
                  width: 20,
                  height: 20,
                  accentColor: colors.primary,
                }}
              />
            </label>

            <button
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                border: 'none',
                backgroundColor: colors.primary + '15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: colors.primary,
              }}
              onClick={() => openEditModal(reminder)}
            >
              ✏️
            </button>

            <button
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                border: 'none',
                backgroundColor: colors.error + '15',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: colors.error,
              }}
              onClick={() => handleDeleteReminder(reminder)}
            >
              🗑️
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing.xxxl,
      textAlign: 'center',
    }}>
      <div style={{
        fontSize: 64,
        color: colors.textTertiary,
        marginBottom: spacing.lg,
      }}>
        🔔
      </div>
      <h2 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.semibold,
        color: colors.text,
        margin: 0,
        marginBottom: spacing.sm,
      }}>
        No Reminders Yet
      </h2>
      <p style={{
        fontSize: typography.fontSize.base,
        color: colors.textSecondary,
        margin: 0,
        marginBottom: spacing.xl,
      }}>
        Create your first reminder to get started
      </p>
      <button
        style={{
          padding: `${spacing.md} ${spacing.lg}`,
          backgroundColor: colors.primary,
          color: colors.white,
          border: 'none',
          borderRadius: borderRadius.md,
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.semibold,
          cursor: 'pointer',
        }}
        onClick={openAddModal}
      >
        Add Reminder
      </button>
    </div>
  );

  const renderAddEditModal = () => {
    if (!showAddModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: colors.surface,
          borderRadius: borderRadius.lg,
          width: '90%',
          maxWidth: 500,
          maxHeight: '90vh',
          overflow: 'auto',
        }}>
          <div style={{
            padding: spacing.lg,
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <button
              style={{
                background: 'none',
                border: 'none',
                color: colors.primary,
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                cursor: 'pointer',
              }}
              onClick={() => {
                setShowAddModal(false);
                setEditingReminder(null);
                resetForm();
              }}
            >
              Cancel
            </button>

            <h2 style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
              margin: 0,
            }}>
              {editingReminder ? 'Edit Reminder' : 'Add Reminder'}
            </h2>

            <button
              style={{
                background: 'none',
                border: 'none',
                color: colors.primary,
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (editingReminder) {
                  handleEditReminder();
                } else {
                  handleAddReminder();
                }
              }}
            >
              {editingReminder ? 'Update' : 'Save'}
            </button>
          </div>

          <div style={{ padding: spacing.lg }}>
            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
                marginBottom: spacing.sm,
              }}>
                Title *
              </label>
              <input
                type="text"
                style={{
                  width: '100%',
                  padding: spacing.md,
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.background,
                  color: colors.text,
                  fontSize: typography.fontSize.base,
                  outline: 'none',
                }}
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter reminder title"
                maxLength={100}
              />
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
                marginBottom: spacing.sm,
              }}>
                Description
              </label>
              <textarea
                style={{
                  width: '100%',
                  padding: spacing.md,
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.background,
                  color: colors.text,
                  fontSize: typography.fontSize.base,
                  outline: 'none',
                  resize: 'vertical',
                  minHeight: 80,
                }}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description (optional)"
                maxLength={500}
                rows={3}
              />
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
                marginBottom: spacing.sm,
              }}>
                Date *
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.background,
              }}>
                <span style={{ marginRight: spacing.sm, color: colors.primary }}>📅</span>
                <input
                  type="date"
                  style={{
                    flex: 1,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: colors.text,
                    fontSize: typography.fontSize.base,
                    outline: 'none',
                  }}
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
                marginBottom: spacing.sm,
              }}>
                Time *
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.background,
              }}>
                <span style={{ marginRight: spacing.sm, color: colors.primary }}>⏰</span>
                <input
                  type="time"
                  style={{
                    flex: 1,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: colors.text,
                    fontSize: typography.fontSize.base,
                    outline: 'none',
                  }}
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text,
                marginBottom: spacing.sm,
              }}>
                Repeat
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: spacing.sm,
              }}>
                {[
                  { key: 'once', label: 'One-time', icon: '⏰' },
                  { key: 'daily', label: 'Daily', icon: '📅' },
                  { key: 'weekly', label: 'Weekly', icon: '📆' },
                  { key: 'monthly', label: 'Monthly', icon: '🗓️' },
                ].map((type) => (
                  <button
                    key={type.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: `${spacing.sm} ${spacing.md}`,
                      borderRadius: borderRadius.xl,
                      border: `1px solid ${formData.type === type.key ? colors.primary : colors.border}`,
                      backgroundColor: formData.type === type.key ? colors.primary + '15' : colors.background,
                      color: formData.type === type.key ? colors.primary : colors.textSecondary,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.medium,
                      cursor: 'pointer',
                      outline: 'none',
                    }}
                    onClick={() => setFormData(prev => ({ ...prev, type: type.key as any }))}
                  >
                    <span style={{ marginRight: spacing.xs }}>{type.icon}</span>
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <label style={{
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.text,
                  marginRight: spacing.md,
                }}>
                  Enable Notifications
                </label>
                <p style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                  margin: 0,
                }}>
                  Receive notifications for this reminder
                </p>
              </div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={formData.isEnabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                  style={{
                    width: 20,
                    height: 20,
                    accentColor: colors.primary,
                  }}
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.lg,
      borderBottom: `1px solid ${colors.border}`,
      marginBottom: spacing.lg,
    },
    headerTitle: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      margin: 0,
    },
    addButton: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.primary,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: 24,
      color: colors.white,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
    loadingText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    listContainer: {
      padding: spacing.md,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Reminders"
        subtitle="Manage your reminders and notifications"
        showBackButton={false}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.header}>
            <h1 style={styles.headerTitle}>Reminders</h1>
            <button style={styles.addButton} onClick={openAddModal}>
              +
            </button>
          </div>

          {loading ? (
            <div style={styles.loadingContainer}>
              <p style={styles.loadingText}>Loading reminders...</p>
            </div>
          ) : (
            <div style={styles.listContainer}>
              {reminders.length > 0 ? (
                reminders.map(renderReminderItem)
              ) : (
                renderEmptyState()
              )}
            </div>
          )}

          {renderAddEditModal()}
        </div>
      </div>
    </div>
  );
};

export default Reminders;